import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ManagerCard from '../components/Manager/ManagerCard';
import AddManagerModal from '../components/Manager/AddManagerModal';
import { getAllManagers, createManager, getBusinessProfile } from '../services/api';
import Toast from '../components/Toast/Toast';
import { useToast } from '../hooks/useToast';
import { useResponsive } from '../hooks/useResponsive';
import { Building2, Phone, Globe, MapPin, Users, Star, TrendingUp, Edit, UserPlus } from 'lucide-react';

function ManagerDashboard() {
  const [managers, setManagers] = useState([]);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toasts, hideToast, success, error, warning } = useToast();
  const { isMobile } = useResponsive();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      // Fetch business profile
      const profileResponse = await getBusinessProfile();
      setBusinessProfile(profileResponse.data);

      // Fetch managers
      const managersResponse = await getAllManagers();
      setManagers(managersResponse.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      if (err.response?.status === 404) {
        warning('Please setup your business profile first');
        setTimeout(() => {
          navigate('/business-profile');
        }, 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  const addManager = async (managerName) => {
    try {
      await createManager({ managerName });
      await fetchData();
      setShowModal(false);
      success('Manager added successfully!');
    } catch (err) {
      error(err.response?.data?.error || 'Failed to create manager');
    }
  };

  const getBusinessTypeLabel = (type) => {
    const types = {
      restaurant: 'Restaurant',
      cafe: 'Cafe',
      salon: 'Salon',
      spa: 'Spa',
      hotel: 'Hotel',
      retail: 'Retail Store',
      other: 'Other'
    };
    return types[type] || type;
  };

  const getTotalReviews = () => {
    return managers.reduce((sum, manager) => sum + (manager.total_reviews || 0), 0);
  };

  const getMonthlyReviews = () => {
    return managers.reduce((sum, manager) => sum + (manager.reviews_this_month || 0), 0);
  };

  return (
    <div className="container">
      {/* Business Info Header */}
      {businessProfile && (
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          marginBottom: isMobile ? '24px' : '32px',
          padding: isMobile ? '20px' : '28px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            flexWrap: 'wrap', 
            gap: '20px',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '250px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <Building2 size={isMobile ? 28 : 32} />
                <h2 style={{ margin: 0, fontSize: isMobile ? '22px' : '28px' }}>{businessProfile.business_name}</h2>
              </div>
              <p style={{ opacity: 0.9, marginBottom: '16px', fontSize: isMobile ? '14px' : '16px' }}>
                {getBusinessTypeLabel(businessProfile.business_type)}
              </p>
              <p style={{ opacity: 0.8, lineHeight: '1.6', marginBottom: '16px', fontSize: isMobile ? '13px' : '15px' }}>
                {businessProfile.description}
              </p>
              <div style={{ display: 'flex', gap: isMobile ? '16px' : '24px', flexWrap: 'wrap', fontSize: isMobile ? '13px' : '14px', opacity: 0.9 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Phone size={16} />
                  <span>{businessProfile.phone}</span>
                </div>
                {businessProfile.website && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Globe size={16} />
                    <a href={businessProfile.website} target="_blank" rel="noopener noreferrer" style={{ color: 'white' }}>Website</a>
                  </div>
                )}
              </div>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '16px',
              width: isMobile ? '100%' : 'auto'
            }}>
              <div style={{ 
                background: 'rgba(255,255,255,0.2)',
                padding: isMobile ? '16px' : '20px',
                borderRadius: '12px',
                textAlign: 'center',
                minWidth: isMobile ? 'auto' : '120px'
              }}>
                <Users size={isMobile ? 20 : 24} style={{ marginBottom: '8px', opacity: 0.9 }} />
                <div style={{ fontSize: isMobile ? '28px' : '32px', fontWeight: 'bold', marginBottom: '4px' }}>
                  {managers.length}
                </div>
                <div style={{ fontSize: isMobile ? '13px' : '14px', opacity: 0.9 }}>Managers</div>
              </div>

              <div style={{ 
                background: 'rgba(255,255,255,0.2)',
                padding: isMobile ? '16px' : '20px',
                borderRadius: '12px',
                textAlign: 'center',
                minWidth: isMobile ? 'auto' : '120px'
              }}>
                <Star size={isMobile ? 20 : 24} style={{ marginBottom: '8px', opacity: 0.9 }} />
                <div style={{ fontSize: isMobile ? '28px' : '32px', fontWeight: 'bold', marginBottom: '4px' }}>
                  {getTotalReviews()}
                </div>
                <div style={{ fontSize: isMobile ? '13px' : '14px', opacity: 0.9 }}>Total Reviews</div>
              </div>

              <div style={{ 
                background: 'rgba(255,255,255,0.2)',
                padding: isMobile ? '16px' : '20px',
                borderRadius: '12px',
                textAlign: 'center',
                minWidth: isMobile ? 'auto' : '120px'
              }}>
                <TrendingUp size={isMobile ? 20 : 24} style={{ marginBottom: '8px', opacity: 0.9 }} />
                <div style={{ fontSize: isMobile ? '28px' : '32px', fontWeight: 'bold', marginBottom: '4px' }}>
                  {getMonthlyReviews()}
                </div>
                <div style={{ fontSize: isMobile ? '13px' : '14px', opacity: 0.9 }}>This Month</div>
              </div>
            </div>
          </div>

          <div style={{ 
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid rgba(255,255,255,0.2)',
            fontSize: isMobile ? '13px' : '14px',
            opacity: 0.9,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <MapPin size={16} />
            <span>{businessProfile.address}</span>
          </div>
        </div>
      )}

      {/* Manager Section Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '16px' : '0'
      }}>
        <h2 style={{ fontSize: isMobile ? '22px' : '28px', margin: 0 }}>Managers</h2>
        <div style={{ 
          display: 'flex', 
          gap: '12px',
          flexDirection: isMobile ? 'column' : 'row',
          width: isMobile ? '100%' : 'auto'
        }}>
          <button 
            className="btn" 
            style={{ 
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
              border: '2px solid #e8ecf1', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              fontWeight: '600',
              justifyContent: 'center',
              fontSize: isMobile ? '14px' : '15px'
            }}
            onClick={() => navigate('/business-profile')}
          >
            <Edit size={isMobile ? 16 : 18} />
            <span>Edit Profile</span>
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowModal(true)} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              fontWeight: '600',
              justifyContent: 'center',
              fontSize: isMobile ? '14px' : '15px'
            }}
          >
            <UserPlus size={isMobile ? 16 : 18} />
            <span>Add New Manager</span>
          </button>
        </div>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', 
        gap: isMobile ? '16px' : '20px' 
      }}>
        {managers.map(manager => (
          <ManagerCard key={manager.id} manager={manager} />
        ))}
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <p style={{ color: '#666' }}>Loading...</p>
        </div>
      ) : managers.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <p style={{ color: '#666' }}>Koi manager nahi hai. Pehla manager add karein!</p>
        </div>
      ) : null}

      {showModal && (
        <AddManagerModal
          onClose={() => setShowModal(false)}
          onAdd={addManager}
        />
      )}

      {/* Toast Notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}
    </div>
  );
}

export default ManagerDashboard;
